import {
  CapacitorBarcodeScanner,
  CapacitorBarcodeScannerCameraDirection,
  CapacitorBarcodeScannerTypeHint,
} from '@capacitor/barcode-scanner';
import { useEffect, useRef } from 'react';
import { isPermissionDeniedError, isValidUrl } from '../../services/utils/cameraUtils';

interface Props {
  isOpen: boolean;
  onScan: (url: string) => void;
  onDismiss: () => void;
  onPermissionDenied: () => void;
  onInvalidUrl: () => void;
}

/**
 * Triggers the @capacitor/barcode-scanner when isOpen becomes true.
 * On web the library builds its own full-screen scanner dialog (appended to document.body)
 * using html5-qrcode internally, so this component renders nothing — the library owns the UI.
 *
 * The library's own close button (id="cap-os-barcode-scanner-close-button") never resolves
 * the scanBarcode() Promise, so we intercept its click to keep our parent state in sync.
 */
export const QrScannerModal = ({ isOpen, onScan, onDismiss, onPermissionDenied, onInvalidUrl }: Props) => {
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;

    cancelledRef.current = false;

    const run = async () => {
      try {
        const result = await CapacitorBarcodeScanner.scanBarcode({
          hint: CapacitorBarcodeScannerTypeHint.QR_CODE,
          cameraDirection: CapacitorBarcodeScannerCameraDirection.BACK,
          web: { showCameraSelection: false, scannerFPS: 10 },
        });

        if (cancelledRef.current) return;

        const text = result.ScanResult;
        if (!text) {
          onDismiss();
          return;
        }

        if (isValidUrl(text)) {
          onScan(text);
        } else {
          onInvalidUrl();
          onDismiss();
        }
      } catch (err) {
        if (cancelledRef.current) return;
        if (isPermissionDeniedError(err)) {
          onPermissionDenied();
        }
        onDismiss();
      }
    };

    run();

    // The library's close button doesn't resolve the Promise — intercept it
    // so closing via the library's own × button propagates onDismiss to the parent.
    const interceptLibraryCloseButton = () => {
      const closeBtn = document.getElementById('cap-os-barcode-scanner-close-button');
      if (closeBtn) {
        closeBtn.addEventListener(
          'click',
          () => {
            if (!cancelledRef.current) {
              cancelledRef.current = true;
              onDismiss();
            }
          },
          { once: true },
        );
      }
    };
    // Small delay to let the library build its DOM before we query it
    const timerId = setTimeout(interceptLibraryCloseButton, 100);

    // @capacitor/barcode-scanner (html5-qrcode internally) swallows the
    // NotAllowedError when the user declines the camera permission mid-scan —
    // the scanBarcode() Promise never rejects. Subscribe to the Permissions API
    // change event so we can detect the denial and clean up ourselves.
    let permissionStatus: PermissionStatus | null = null;
    const handlePermissionChange = () => {
      if (permissionStatus?.state === 'denied' && !cancelledRef.current) {
        cancelledRef.current = true;
        // When permission is denied mid-scan, html5-qrcode has already stopped its
        // internal scanner, so clicking the close button throws "Cannot stop, scanner
        // is not running". Remove all library-injected DOM elements directly instead
        // (container, backdrop, and any siblings the library appended to body).
        document.querySelectorAll('[id^="cap-os-barcode-scanner"]').forEach((el) => el.remove());
        onPermissionDenied();
        onDismiss();
      }
    };

    navigator?.permissions
      ?.query({ name: 'camera' as PermissionName })
      .then((status) => {
        permissionStatus = status;
        status.addEventListener('change', handlePermissionChange);
      })
      .catch(() => console.log('Error on handling permission change'));

    return () => {
      clearTimeout(timerId);
      permissionStatus?.removeEventListener('change', handlePermissionChange);
      if (!cancelledRef.current) {
        cancelledRef.current = true;
        // Trigger the library's close button programmatically to stop the camera stream
        document.getElementById('cap-os-barcode-scanner-close-button')?.click();
      }
    };
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Library handles all UI — nothing to render
  return null;
};

// =============================================================================
// FALLBACK — html5-qrcode implementation
// Uncomment this entire block (and comment out the implementation above) if
// @capacitor/barcode-scanner causes issues on web.
// Requires: npm install html5-qrcode
// =============================================================================
//
// import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/react';
// import { Html5Qrcode } from 'html5-qrcode';
// import { useCallback, useRef } from 'react';
// import { useTranslation } from 'react-i18next';
// import { isPermissionDeniedError, isValidUrl } from '../../services/utils/cameraUtils';
//
// const SCANNER_DIV_ID = 'qr-scanner-element';
//
// export const QrScannerModal = ({ isOpen, onScan, onDismiss, onPermissionDenied, onInvalidUrl }: Props) => {
//   const { t } = useTranslation();
//   const scannerRef = useRef<Html5Qrcode | null>(null);
//
//   const stopScanner = useCallback(async () => {
//     try {
//       if (scannerRef.current?.isScanning) await scannerRef.current.stop();
//       scannerRef.current?.clear();
//     } catch { /* ignore */ }
//     scannerRef.current = null;
//   }, []);
//
//   const startScanner = useCallback(async () => {
//     try {
//       const scanner = new Html5Qrcode(SCANNER_DIV_ID, { verbose: false });
//       scannerRef.current = scanner;
//       await scanner.start(
//         { facingMode: 'environment' },
//         { fps: 10, qrbox: { width: 250, height: 250 } },
//         async (text) => {
//           await stopScanner();
//           if (isValidUrl(text)) { onScan(text); }
//           else { onInvalidUrl(); onDismiss(); }
//         },
//         () => {},
//       );
//     } catch (err) {
//       await stopScanner();
//       if (isPermissionDeniedError(err)) { onPermissionDenied(); }
//       else { onDismiss(); }
//     }
//   }, [onDismiss, onInvalidUrl, onPermissionDenied, onScan, stopScanner]);
//
//   const handleDismiss = useCallback(async () => {
//     await stopScanner();
//     onDismiss();
//   }, [onDismiss, stopScanner]);
//
//   return (
//     <IonModal isOpen={isOpen} onDidPresent={startScanner} onWillDismiss={stopScanner}>
//       <IonHeader>
//         <IonToolbar>
//           <IonTitle>{t('CAMERA.SCAN_QR_TITLE')}</IonTitle>
//           <IonButtons slot="end">
//             <IonButton onClick={handleDismiss}>{t('CAMERA.CANCEL')}</IonButton>
//           </IonButtons>
//         </IonToolbar>
//       </IonHeader>
//       <IonContent>
//         <div id={SCANNER_DIV_ID} style={{ width: '100%', minHeight: 300 }} />
//       </IonContent>
//     </IonModal>
//   );
// };
